import type { IFilterValues } from '@intelligence/v3/filterCore/types';
import type { IFilterInitMeta, IFilterSyncResult } from '@intelligence/v3/_hooks/useFilterablePage/types';

export type IFilterablePageDemoValues = IFilterValues;

export interface IFilterablePageDemoPanelItem {
  key: string;
  title: string;
  value: Record<string, any> | string;
}

export interface IMockFetchOptionsResult {
  regionOptions: any[];
  platformOptions: any[];
  engineOptions: any[];
  sensorTowerTagOptions: Record<string, any[]>;
  sensorTowerThemeOptions: any[];
  appmagicTagOptions: Record<string, any[]>;
}

/**
 * 组装 Demo 调试面板数据。
 * 面板展示值只用于观察链路，不反向参与 Filter 状态。
 */
export const buildFilterablePageDemoPanelItems = (params: {
  draftValues: IFilterablePageDemoValues;
  filterValues: IFilterablePageDemoValues;
  apiParams: Record<string, any>;
  currentQuery: string;
  initMeta: IFilterInitMeta | null;
  syncResult: IFilterSyncResult;
  confirmedTitle: string;
}): IFilterablePageDemoPanelItem[] => {
  const { draftValues, filterValues, apiParams, currentQuery, initMeta, syncResult, confirmedTitle } = params;

  return [
    {
      key: 'draft',
      title: 'Draft Value（编辑中的值）',
      value: draftValues,
    },
    {
      key: 'confirmed',
      title: confirmedTitle,
      value: filterValues,
    },
    {
      key: 'api',
      title: 'API Params（接口入参）',
      value: apiParams,
    },
    {
      key: 'url',
      title: 'Current URL Query',
      value: currentQuery,
    },
    {
      key: 'initMeta',
      title: 'Init Sources（初始化来源命中）',
      value: initMeta || {},
    },
    {
      key: 'syncResult',
      title: 'Sync Result（最近一次同步结果）',
      value: syncResult,
    },
  ];
};

/** 模拟页面依赖的筛选器 options 数据 */
export const mockFetchOptions = (): Promise<IMockFetchOptionsResult> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        regionOptions: [
          {
            area_code: 'east_asia',
            area_name_en: 'East Asia',
            area_name_ch: '东亚',
            country_set: [
              {
                country_code: 'CN',
                country_name_en: 'China',
                country_name_ch: '中国',
              },
              {
                country_code: 'JP',
                country_name_en: 'Japan',
                country_name_ch: '日本',
              },
              {
                country_code: 'KR',
                country_name_en: 'South Korea',
                country_name_ch: '韩国',
              },
            ],
          },
          {
            area_code: 'north_america',
            area_name_en: 'North America',
            area_name_ch: '北美',
            country_set: [
              {
                country_code: 'US',
                country_name_en: 'United States',
                country_name_ch: '美国',
              },
              {
                country_code: 'CA',
                country_name_en: 'Canada',
                country_name_ch: '加拿大',
              },
            ],
          },
          {
            area_code: 'europe',
            area_name_en: 'Europe',
            area_name_ch: '欧洲',
            country_set: [
              {
                country_code: 'GB',
                country_name_en: 'United Kingdom',
                country_name_ch: '英国',
              },
              {
                country_code: 'DE',
                country_name_en: 'Germany',
                country_name_ch: '德国',
              },
              {
                country_code: 'FR',
                country_name_en: 'France',
                country_name_ch: '法国',
              },
            ],
          },
        ],
        platformOptions: [
          { value: 'appstore', text: 'App Store' },
          { value: 'googleplay', text: 'Google Play' },
        ],
        engineOptions: [
          { value: 'unity', text: 'Unity' },
          { value: 'unreal', text: 'Unreal Engine' },
          { value: 'cocos', text: 'Cocos' },
        ],
        sensorTowerTagOptions: {
          sensor_tower_tag_art_style: [
            {
              value: 'Anime',
              text: 'Anime',
              textCN: '二次元',
            },
            {
              value: 'Pixel Graphics',
              text: 'Pixel Graphics',
              textCN: '像素风',
            },
            {
              value: 'Realistic',
              text: 'Realistic',
              textCN: '写实',
            },
            {
              value: 'Cartoon',
              text: 'Cartoon',
              textCN: '卡通',
            },
          ],
          sensor_tower_tag_camera_pov: [
            {
              value: 'Top-Down',
              text: 'Top-Down',
              textCN: '俯视角',
            },
            {
              value: 'Side View',
              text: 'Side View',
              textCN: '侧视角',
            },
            {
              value: 'Third Person',
              text: 'Third Person',
              textCN: '第三人称',
            },
          ],
          sensor_tower_tag_product_model: [
            {
              value: 'Single Player',
              text: 'Single Player',
              textCN: '单人',
            },
            {
              value: 'Multiplayer',
              text: 'Multiplayer',
              textCN: '多人',
            },
            {
              value: 'Live Ops',
              text: 'Live Ops',
              textCN: '长线运营',
            },
          ],
          sensor_tower_tag_setting: [
            {
              value: 'Fantasy',
              text: 'Fantasy',
              textCN: '奇幻',
            },
            {
              value: 'Post-apocalyptic',
              text: 'Post-apocalyptic',
              textCN: '后启示录',
            },
            {
              value: 'Modern City',
              text: 'Modern City',
              textCN: '现代都市',
            },
          ],
          sensor_tower_tag_tags: [
            {
              value: 'Gacha',
              text: 'Gacha',
              textCN: '抽卡',
            },
            {
              value: 'Idle',
              text: 'Idle',
              textCN: '放置',
            },
            {
              value: 'Match-3',
              text: 'Match-3',
              textCN: '三消',
            },
            {
              value: 'Tycoon',
              text: 'Tycoon',
              textCN: '模拟经营',
            },
          ],
          sensor_tower_tag_monetization: [
            {
              value: 'Battle Pass',
              text: 'Battle Pass',
              textCN: '战斗通行证',
            },
            {
              value: 'In-App Purchase',
              text: 'In-App Purchase',
              textCN: '应用内购',
            },
            {
              value: 'Ads',
              text: 'Ads',
              textCN: '广告变现',
            },
          ],
        },
        sensorTowerThemeOptions: [
          {
            value: 'RPG',
            text: 'RPG',
            textCN: '角色扮演',
            children: [
              {
                value: 'RPG@Fantasy',
                text: 'Fantasy',
                textCN: '奇幻',
              },
              {
                value: 'RPG@Dungeon',
                text: 'Dungeon',
                textCN: '地牢',
              },
              {
                value: 'RPG@Open World',
                text: 'Open World',
                textCN: '开放世界',
              },
            ],
          },
          {
            value: 'Strategy',
            text: 'Strategy',
            textCN: '策略',
            children: [
              {
                value: 'Strategy@4X',
                text: '4X',
                textCN: '4X',
              },
              {
                value: 'Strategy@War Simulation',
                text: 'War Simulation',
                textCN: '战争模拟',
              },
              {
                value: 'Strategy@Empire Builder',
                text: 'Empire Builder',
                textCN: '帝国经营',
              },
            ],
          },
          {
            value: 'Simulation',
            text: 'Simulation',
            textCN: '模拟',
            children: [
              {
                value: 'Simulation@Farm',
                text: 'Farm',
                textCN: '农场',
              },
              {
                value: 'Simulation@Life Sim',
                text: 'Life Sim',
                textCN: '生活模拟',
              },
              {
                value: 'Simulation@Restaurant',
                text: 'Restaurant',
                textCN: '餐厅经营',
              },
            ],
          },
        ],
        appmagicTagOptions: {
          appmagic_themes: [
            {
              value: 'Cozy',
              text: 'Cozy',
              textCN: '治愈',
            },
            {
              value: 'Fashion',
              text: 'Fashion',
              textCN: '时尚',
            },
            {
              value: 'Survival',
              text: 'Survival',
              textCN: '生存',
            },
          ],
          appmagic_features: [
            {
              value: 'Merge',
              text: 'Merge',
              textCN: '合成',
            },
            {
              value: 'Deck Building',
              text: 'Deck Building',
              textCN: '卡组构筑',
            },
            {
              value: 'Base Building',
              text: 'Base Building',
              textCN: '基地建设',
            },
          ],
          appmagic_ip: [
            {
              value: 'Anime',
              text: 'Anime',
              textCN: '动漫IP',
            },
            {
              value: 'Superhero',
              text: 'Superhero',
              textCN: '超级英雄',
            },
            {
              value: 'Sports',
              text: 'Sports',
              textCN: '体育IP',
            },
          ],
          appmagic_settings: [
            {
              value: 'Medieval',
              text: 'Medieval',
              textCN: '中世纪',
            },
            {
              value: 'Cyberpunk',
              text: 'Cyberpunk',
              textCN: '赛博朋克',
            },
            {
              value: 'School Life',
              text: 'School Life',
              textCN: '校园生活',
            },
          ],
          appmagic_art_styles: [
            {
              value: 'Low Poly',
              text: 'Low Poly',
              textCN: '低多边形',
            },
            {
              value: 'Chibi',
              text: 'Chibi',
              textCN: 'Q版',
            },
            {
              value: 'Hand-painted',
              text: 'Hand-painted',
              textCN: '手绘',
            },
          ],
        },
      });
    }, 1500);
  });
};
